import { useState } from 'react';
import type { LinkStatusView } from '@reform-lite/contracts';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useT } from '@/i18n/use-t';
import type { Ip3Source } from '@/hooks/use-ip3-sources';
import { useLinkStatus } from '@/hooks/use-link-status';
import { CrmReceiptRows } from './crm-receipt-actions';

/**
 * RL-936 — what a store's Link last reported about its CRM receipt outbox:
 * carded receipts waiting for the CRM, held for a person, or refused by it;
 * since when the oldest one waits; and the last error, in plain words.
 *
 * A setup error (the CRM does not know the store's trade point or cash desk)
 * is retried until someone fixes the setup, so a receipt that waits over a
 * day is a warning, as is any held or refused one. Back Office cannot reach
 * a Link: everything here is its last report, and a report that is not fresh
 * is not shown, so an old count never reads as the current one.
 */
export function CrmReceiptStatusPanel({
  sources,
}: {
  /** The stores that have a CRM loyalty source. */
  sources: readonly Ip3Source[];
}): JSX.Element | null {
  const t = useT('admin_ui');
  const [picked, setPicked] = useState<string | null>(null);
  const stores = sources.filter(
    (row, index) => sources.findIndex((other) => other.location.id === row.location.id) === index,
  );
  const store = stores.find((row) => row.location.id === picked) ?? stores[0] ?? null;
  if (store === null) return null;

  return (
    <section
      aria-label={t('settings.integrations.ip3.status.title')}
      className="space-y-3 border-t border-border pt-5"
    >
      <div>
        <h3 className="text-sm font-semibold">{t('settings.integrations.ip3.status.title')}</h3>
        <p className="max-w-3xl text-xs text-muted-foreground">
          {t('settings.integrations.ip3.status.description')}
        </p>
      </div>
      {stores.length > 1 && (
        <div className="flex max-w-md items-center gap-2">
          <Label htmlFor="crm-receipt-status-store">
            {t('settings.integrations.ownership.store')}
          </Label>
          <Select
            id="crm-receipt-status-store"
            value={store.location.id}
            onChange={(event) => setPicked(event.target.value)}
          >
            {stores.map((row) => (
              <option key={row.location.id} value={row.location.id}>
                {row.location.name} ({row.location.code})
              </option>
            ))}
          </Select>
        </div>
      )}
      <OutboxStatus key={store.location.id} locationId={store.location.id} />
    </section>
  );
}

/** A receipt waiting longer than this is a warning. */
const WAITING_TOO_LONG_MS = 24 * 60 * 60 * 1000;

/**
 * The Link's error keys an operator gets words for. Any other key is shown
 * as the code itself: it is a stable code, never the CRM's text.
 */
const REASON_KEYS: ReadonlySet<string> = new Set([
  'CannotFindAccountByCode',
  'CannotFindCashDeskByCode',
  'CannotFindPurchaseChanel',
  'register_missing',
  'timeout',
  'network',
  'http_5xx',
  'CannotFindCardByNumber',
  'CardNumberIsBlock',
  'CardIsNotActive',
  'CannotFindProductWithCode',
  'validation',
  'reconcile_missing',
  'original_unknown',
]);

function when(value: string | null): string {
  if (value === null) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(value),
  );
}

function OutboxStatus({ locationId }: { locationId: string }): JSX.Element {
  const t = useT('admin_ui');
  const query = useLinkStatus(locationId);
  const view: LinkStatusView | undefined = query.data;

  if (query.isError) {
    return (
      <p role="alert" className="text-xs text-destructive">
        {t('settings.integrations.ip3.status.loadError')}
      </p>
    );
  }
  if (view === undefined) {
    return (
      <p className="text-xs text-muted-foreground">
        {t('settings.integrations.ip3.status.loading')}
      </p>
    );
  }
  if (view.device === null) {
    return (
      <p className="text-xs text-muted-foreground">
        {t('settings.integrations.exportStatus.noLink')}
      </p>
    );
  }
  // Never reported, or not recently: nothing the Link said earlier is current.
  if (view.state === 'missing' || view.state === 'stale') {
    return (
      <p role="status" className="text-xs text-destructive">
        {t('settings.integrations.ip3.status.stale')}
      </p>
    );
  }
  const crm = view.crm ?? null;
  if (crm === null || crm.receipts === null) {
    return (
      <p role="status" className="text-xs text-muted-foreground">
        {t('settings.integrations.ip3.status.notReported')}
      </p>
    );
  }
  const receipts = crm.receipts;
  const reasonKey = receipts.lastErrorKey ?? crm.lastErrorKey;
  // A key in plain words when there are words for it; else the stable code itself.
  const reason = (key: string | null): string =>
    key === null
      ? '—'
      : REASON_KEYS.has(key)
        ? t(`settings.integrations.ip3.status.reason.${key}`)
        : key;
  const waitingTooLong =
    receipts.oldestPendingAt !== null &&
    Date.now() - new Date(receipts.oldestPendingAt).getTime() > WAITING_TOO_LONG_MS;
  const warn = waitingTooLong || receipts.held > 0 || receipts.rejected > 0;

  return (
    <div className="space-y-3 rounded-md border border-border p-3 text-sm">
      {warn && (
        <p role="alert" className="text-xs text-destructive">
          {t('settings.integrations.ip3.status.warning')}
        </p>
      )}
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs">
        <dt className="text-muted-foreground">{t('settings.integrations.ip3.status.pending')}</dt>
        <dd data-testid="crm-receipts-pending">{receipts.pending}</dd>
        <dt className="text-muted-foreground">
          {t('settings.integrations.ip3.status.oldestPending')}
        </dt>
        <dd className={waitingTooLong ? 'text-destructive' : undefined}>
          {when(receipts.oldestPendingAt)}
        </dd>
        <dt className="text-muted-foreground">{t('settings.integrations.ip3.status.held')}</dt>
        <dd
          data-testid="crm-receipts-held"
          className={receipts.held > 0 ? 'text-destructive' : undefined}
        >
          {receipts.held}
        </dd>
        <dt className="text-muted-foreground">{t('settings.integrations.ip3.status.rejected')}</dt>
        <dd
          data-testid="crm-receipts-rejected"
          className={receipts.rejected > 0 ? 'text-destructive' : undefined}
        >
          {receipts.rejected}
        </dd>
        <dt className="text-muted-foreground">{t('settings.integrations.ip3.status.lastError')}</dt>
        <dd data-testid="crm-receipts-reason" className="break-words">
          {reason(reasonKey)}
        </dd>
        <dt className="text-muted-foreground">
          {t('settings.integrations.exportStatus.reportedAt')}
        </dt>
        <dd>{when(crm.reportedAt)}</dd>
      </dl>
      {/* RL-990: the held and refused receipts themselves, with what an operator may do. */}
      <CrmReceiptRows
        locationId={locationId}
        view={view}
        problems={receipts.problems ?? []}
        reason={reason}
      />
    </div>
  );
}
